import {
  Box,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  Switch,
  Typography,
} from "@mui/material";
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import dayjs from "dayjs";
import { debounce, isEmpty } from "lodash";

import fontWeight from "../../../../styles/mui/fontWeight";
import colors from "../../../../styles/colors/colors";
import DatePicker from "../../../../components/formComponents/DatePicker";
import React, { useEffect, useState } from "react";
import { FormInputText } from "../../../../components/formComponents/FormInputText";
import {
  CareTypeStyle,
  DescriptionStyle,
  DisciplineStyle,
  DisplayBlock,
  OptionsContainer,
  PaddingTopBtm1rem,
  RequiredSymbol,
  TextArea,
} from "./style";
import {
  CareType,
  Discipline,
  DisciplineMaster,
  DisciplineSearch,
} from "../../../../models/ServiceRequest";
import { TabSessionStorageResponse } from "../../../../models/Api/TabSessionStorage";
import {
  CareTypeResponse,
  DisciplinesResponse,
} from "../../../../models/Api/ServiceRequest";
import { getValue, is21OrTurning21Soon, length } from "../../../../utils";
import { rules } from "../../../../utils/validations/Validations";
import { EligibilityInformation } from "../../../../models/CheckEligibility";
import { formatDate } from "../../../../utils/DateTime";
import { DateFormats } from "../../../../constants/Date";
import { displayFlex } from "../../../../styles/mui/styles/display";

export interface PropsFromState {
  control: Control<any, any>;
  TabSessionStorageData: TabSessionStorageResponse;
  careTypeData: CareTypeResponse;
  disciplineData: DisciplinesResponse;
  setValue: UseFormSetValue<any>;
  getValues: UseFormGetValues<any>;
  eligibilityData: EligibilityInformation | null;
  formState: any;
}
export interface PropsFromDispatch {
  getDisciplines: (payload: DisciplineSearch) => void;
  getCareType: () => void;
}
type AllProps = PropsFromState & PropsFromDispatch;

const ServiceRequest: React.FC<any> = ({
  control,
  getDisciplines,
  getCareType,
  disciplineData,
  careTypeData,
  setValue,
  TabSessionStorageData,
  getValues,
  eligibilityData,
  formState,
}: AllProps) => {
  const [disciplinesRecord, setDisciplinesRecord] =
    useState<DisciplineMaster | null>(null);
  const [searchCareType, setSearchCareType] = useState<CareType[] | []>();
  const [disciplineList, setDisciplineList] = useState<Discipline[] | []>();
  const [careTypeList, setCareTypeList] = useState<CareType[] | []>();
  const [isSt, setIsSt] = useState(false);
  const [isPatientGoingToBe21, setIsPatientGoingToBe21] = useState(false);
  const { response } = TabSessionStorageData;
  const { loading: CareTypeLoading, response: CareTypeResponse } = careTypeData;
  const { loading: DisciplineLoading, response: DisciplineResponse } =
    disciplineData;

  const maximumDate = dayjs().add(60, "day");

  useEffect(() => {
    if (!CareTypeLoading) {
      getCareType();
    }
    const payload: DisciplineSearch = {
      eligibilityId: getValue(eligibilityData, "eligibilityId")
        ? getValue(eligibilityData, "eligibilityId")
        : 0,
      socDate: getValue(response, "SessionData.serviceRequest.startOfCareDate")
        ? formatDate(
            getValue(response, "SessionData.serviceRequest.startOfCareDate"),
            DateFormats.MM_DD_YYYY
          )
        : null,
    };
    if (!DisciplineLoading && getValue(payload, "eligibilityId") > 0) {
      getDisciplines(payload);
    }
  }, []);

  useEffect(() => {
    if (CareTypeResponse) {
      setSearchCareType(CareTypeResponse);
    }
    if (DisciplineResponse) {
      setDisciplinesRecord(DisciplineResponse);
    }
  }, [CareTypeResponse, DisciplineResponse]);

  useEffect(() => {
    if (
      getValue(disciplinesRecord, "discipline") &&
      getValue(disciplinesRecord, "discipline").some(
        (data: Discipline) => data.azCompeleteCheck === true
      )
    ) {
      if (
        is21OrTurning21Soon(
          getValue(response, "SessionData.patientDetail.dateOfBirth"),
          getValue(disciplinesRecord, "startDate"),
          getValue(disciplinesRecord, "endDate")
        )
      ) {
        setIsPatientGoingToBe21(true);
      } else {
        setIsPatientGoingToBe21(false);
      }
    }
  }, [response, disciplinesRecord]);

  useEffect(() => {
    setValue("serviceRequest.discipline", disciplineList);
    setValue("serviceRequest.careType", careTypeList);
  }, [careTypeList, disciplineList]);

  useEffect(() => {
    if (length(getValue(response, "SessionData.serviceRequest.careType"))) {
      setCareTypeList(
        getValue(response, "SessionData.serviceRequest.careType")
      );
    }
    if (length(getValue(response, "SessionData.serviceRequest.discipline"))) {
      setDisciplineList(
        getValue(response, "SessionData.serviceRequest.discipline")
      );
    }
  }, [response]);

  const handleCareTypeList = (e: any) => {
    const careTypes = careTypeList ? careTypeList : [];

    const careTypeUnderIds =
      careTypes &&
      length(careTypes) &&
      careTypes.map((careType) => careType.careTypeId);

    if (careTypeUnderIds && careTypeUnderIds.includes(Number(e.target.value))) {
      const newCareType =
        careTypes && length(careTypes)
          ? careTypes.filter((item) => {
              return item.careTypeId != e.target.value;
            })
          : [];
      const caretypesList =
        searchCareType &&
        searchCareType.filter((discipline: CareType) =>
          newCareType.some(
            (newDiscipline) =>
              discipline.careTypeId === newDiscipline.careTypeId
          )
        );
      setCareTypeList(caretypesList);
      return;
    }
    const careType = { careTypeId: Number(e.target.value) };
    const newArrWithAddedCareTypeId = [...careTypes, careType];
    const caretypesList =
      searchCareType &&
      searchCareType.filter((discipline: CareType) =>
        newArrWithAddedCareTypeId.some(
          (newDiscipline) => discipline.careTypeId === newDiscipline.careTypeId
        )
      );
    setCareTypeList(caretypesList);
  };

  const handleDisciplinesList = (e: any) => {
    const disciplines = disciplineList ? disciplineList : [];

    const disciplinesUnderIds =
      disciplines &&
      length(disciplines) &&
      disciplines.map((disc) => disc.disciplineId);

    const selectedDiscipline: Discipline = getValue(
      disciplinesRecord,
      "discipline"
    ).filter(
      (discipline: Discipline) =>
        discipline.disciplineId === Number(e.target.value)
    )[0];

    if (
      disciplinesUnderIds &&
      disciplinesUnderIds.includes(Number(e.target.value))
    ) {
      const newDisciplines =
        disciplines && length(disciplines)
          ? disciplines.filter((item) => {
              return item.disciplineId != e.target.value;
            })
          : [];
      if (selectedDiscipline.azCompeleteCheck) {
        setIsSt(false);
      }
      const newDisciplinesList =
        newDisciplines && length(newDisciplines)
          ? newDisciplines.map((item) => ({
              disciplineId: item.disciplineId,
            }))
          : [];
      const disciplinesList = getValue(disciplinesRecord, "discipline").filter(
        (discipline: Discipline) =>
          newDisciplinesList.some(
            (newDiscipline) =>
              discipline.disciplineId === newDiscipline.disciplineId
          )
      );
      setDisciplineList(disciplinesList);
      return;
    }
    if (selectedDiscipline.azCompeleteCheck) {
      setIsSt(true);
    }
    const discipline = {
      disciplineId: Number(e.target.value),
    };
    const newDisciplinesList =
      disciplines && length(disciplines)
        ? disciplines.map((item) => ({
            disciplineId: item.disciplineId,
          }))
        : [];
    const newArrWithAddedDiscipline = [...newDisciplinesList, discipline];
    const disciplinesList = getValue(disciplinesRecord, "discipline").filter(
      (discipline: Discipline) =>
        newArrWithAddedDiscipline.some(
          (newDiscipline) =>
            discipline.disciplineId === newDiscipline.disciplineId
        )
    );
    setDisciplineList(disciplinesList);
  };

  const handleDateChange = debounce((e: Date) => {
    const newDate = formatDate(e, DateFormats.YYYY_MM_DD);
    const selectedDisciplineList = getValue(disciplinesRecord, "discipline")
      ? getValue(disciplinesRecord, "discipline")
      : [];
    if (
      Object.prototype.hasOwnProperty.call(
        formState.errors,
        "startOfCareDate"
      ) === false &&
      selectedDisciplineList.some(
        (discipline: Discipline) => discipline.azCompeleteCheck
      )
    ) {
      const payload: DisciplineSearch = {
        eligibilityId: getValue(eligibilityData, "eligibilityId")
          ? getValue(eligibilityData, "eligibilityId")
          : 0,
        socDate: newDate,
      };
      if (!DisciplineLoading && getValue(payload, "eligibilityId") > 0) {
        getDisciplines(payload);
        setValue("serviceRequest.discipline", []);
        setDisciplineList([]);
        setIsSt(false);
      }
    }
  }, 500);

  const getDisciplineLabel = (
    disciplineDescription: string | null,
    pairedDisciplineDescription: string | null
  ) => {
    return (
      <Box sx={displayFlex} gap={"0.3rem"}>
        <Typography
          variant="subtitle2"
          color={colors.fonts[4]}
          fontWeight={fontWeight.Weight[4]}
        >
          {disciplineDescription}
        </Typography>
        {!isEmpty(pairedDisciplineDescription) && (
          <Typography
            variant="body1"
            color={colors.fonts[2]}
            fontWeight={fontWeight.Weight[3]}
          >
            {`(${pairedDisciplineDescription})`}
          </Typography>
        )}
      </Box>
    );
  };
  return (
    <Grid container pl={"2rem"}>
      <Grid size={12}>
        <Box>
          <Typography
            variant="h5"
            fontWeight={fontWeight.Weight[4]}
            color={colors.black[2]}
          >
            Select the Care Type and the Discipline
          </Typography>
        </Box>
      </Grid>
      <Grid size={12} sx={PaddingTopBtm1rem}>
        <FormControl variant="standard">
          <DatePicker
            name="serviceRequest.startOfCareDate"
            control={control}
            label={"Requested SOC Date:"}
            fieldrequired={"required"}
            helper={rules.referralSourceDischargeDate}
            allowFutureDate={true}
            maximumdate={maximumDate}
            minimumDate={
              isEmpty(dayjs(getValues("serviceRequest.startOfCareDate")))
                ? dayjs(getValues("serviceRequest.startOfCareDate"))
                : dayjs()
            }
            onChangeHandler={handleDateChange}
          />
        </FormControl>
      </Grid>
      <Grid size={12} sx={PaddingTopBtm1rem}>
        <Grid container>
          <Typography
            variant="h6"
            fontWeight={fontWeight.Weight[4]}
            color={colors.black[2]}
          >
            Care Type Required:
          </Typography>
        </Grid>
        <Grid container sx={OptionsContainer}>
          <Controller
            defaultValue=""
            render={({ field, fieldState: { error } }) => (
              <Grid size={12}>
                <FormGroup
                  {...field}
                  onChange={handleCareTypeList}
                  sx={CareTypeStyle}
                >
                  {searchCareType &&
                    length(searchCareType) &&
                    searchCareType.map((item: CareType) => {
                      const careTypeIds =
                        careTypeList &&
                        length(careTypeList) &&
                        careTypeList.map(
                          (careType: CareType) => careType.careTypeId
                        );
                      return (
                        <Grid size={2.5} key={item.careTypeId} p={"0.3rem 0"}>
                          <FormControlLabel
                            control={
                              <Switch
                                value={item.careTypeId}
                                checked={
                                  careTypeIds &&
                                  careTypeIds.includes(item.careTypeId)
                                    ? true
                                    : false
                                }
                              />
                            }
                            label={
                              <Typography
                                variant="subtitle2"
                                color={colors.fonts[4]}
                                fontWeight={fontWeight.Weight[4]}
                              >
                                {item.careTypeName}
                              </Typography>
                            }
                          />
                        </Grid>
                      );
                    })}
                </FormGroup>
                <FormHelperText>{error ? error.message : null}</FormHelperText>
              </Grid>
            )}
            name={`serviceRequest.careType`}
            control={control}
          />
        </Grid>
        <Grid container sx={DisplayBlock}>
          <Typography
            variant="body1"
            fontWeight={fontWeight.Weight[3]}
            color={colors.black[2]}
            pb={"0.1rem"}
          >
            PICC line care*
          </Typography>
          <Typography
            variant="body1"
            fontWeight={fontWeight.Weight[3]}
            color={colors.black[2]}
            pb={"0.1rem"}
          >
            Behavioral Health/Psychiatric*
          </Typography>
          <Typography
            variant="body1"
            fontWeight={fontWeight.Weight[3]}
            color={colors.black[2]}
            pb={"0.1rem"}
          >
            Home Infusion Therapy*
          </Typography>
          <Typography
            variant="body1"
            fontWeight={fontWeight.Weight[3]}
            color={colors.black[2]}
            pb={"0.1rem"}
          >
            Tango is not contracted, these care types must be coordinated
            through the Health Plan*
          </Typography>
        </Grid>
      </Grid>
      <Grid size={12} sx={PaddingTopBtm1rem}>
        <Grid container sx={DisplayBlock}>
          <Box sx={RequiredSymbol}>
          <Typography
            variant="h6"
            fontWeight={fontWeight.Weight[4]}
            color={colors.black[2]}
          >
            Ordered Disciplines:
            </Typography>
            <Typography
              variant="subtitle1"
              color={colors.red[100]}
              pl={"0.2rem"}
            >
              *
            </Typography>
          </Box>
          <Typography
            variant="subtitle2"
            fontWeight={fontWeight.Weight[3]}
            color={colors.fonts[2]}
            pt={"0.5rem"}
          >
            Which skilled disciplines are ordered for start of care? CHOOSE ALL
            THAT APPLY
          </Typography>
        </Grid>
        <Grid container sx={OptionsContainer}>
          <Grid size={8}>
            <Controller
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <Grid size={12}>
                  <FormGroup
                    {...field}
                    onChange={handleDisciplinesList}
                    sx={DisciplineStyle}
                  >
                    {getValue(disciplinesRecord, "discipline") &&
                      length(getValue(disciplinesRecord, "discipline")) &&
                      getValue(disciplinesRecord, "discipline").map(
                        (item: Discipline) => {
                          const disciplieIds =
                            disciplineList &&
                            length(disciplineList) &&
                            disciplineList.map(
                              (dis: Discipline) => dis.disciplineId
                            );
                          return (
                            <Grid
                              size={
                                isEmpty(item.pairedDisciplinesDescription)
                                  ? 4
                                  : 8
                              }
                              key={item.disciplineId}
                              p={"0.3rem 0"}
                            >
                              <FormControlLabel
                                control={
                                  <Switch
                                    value={item.disciplineId}
                                    checked={
                                      disciplieIds &&
                                      disciplieIds.includes(item.disciplineId)
                                        ? true
                                        : false
                                    }
                                  />
                                }
                                label={getDisciplineLabel(
                                  item.disciplineDescription,
                                  item.pairedDisciplinesDescription
                                )}
                              />
                            </Grid>
                          );
                        }
                      )}
                  </FormGroup>
                  <FormHelperText>
                    {error ? error.message : null}
                    {getValue(disciplinesRecord, "isMedicaid") &&
                      isPatientGoingToBe21 &&
                      isSt && (
                        <Box>
                          <Typography
                            variant="body1"
                            color={colors.red[90]}
                            fontWeight={fontWeight.Weight[3]}
                          >
                            AZ Complete Medicaid members do not have speech
                            therapy coverage after their 21st birthday. This
                            request has dates in which the member will turn 21
                            years old,
                          </Typography>
                        </Box>
                      )}
                  </FormHelperText>
                </Grid>
              )}
              name={`serviceRequest.discipline`}
              control={control}
              rules={rules.discipline}
            />
          </Grid>
        </Grid>
        <Grid container>
          <FormControl sx={DescriptionStyle} variant="standard">
            <FormInputText
              name="serviceRequest.needDescription"
              control={control}
              placeholder="Describe the need for skilled services in the home."
              inputVariant="outlined"
              inputRows={4}
              multiline={true}
              textLength={250}
              InputStyle={TextArea}
            />
          </FormControl>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ServiceRequest;
